import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * Presentation Mode Context
 * 
 * When enabled, masks sensitive data across the entire app:
 * - Tenant/Client names → "Client A", "Client B", etc.
 * - Email addresses → "user***@***.com"
 * - Contact names → "Contact 1", "Contact 2"
 * - Deal amounts → "$***"
 * - Phone numbers → "***-***-****"
 * 
 * Usage:
 *   const { isPresentationMode, maskValue, maskTenant } = usePresentationMode();
 * 
 * Tenant Permission:
 *   Super admins can enable/disable presentation mode per tenant in Settings.
 *   If not enabled for a tenant, the toggle and modal won't appear.
 */

const PresentationModeContext = createContext(null);

// Alphabet for tenant masking
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export const PresentationModeProvider = ({ children }) => {
  // ALWAYS start masked (true) - user must explicitly unmask
  const [isPresentationMode, setIsPresentationMode] = useState(true);
  
  // Cache for consistent tenant masking
  const [tenantMaskCache, setTenantMaskCache] = useState({});
  let tenantCounter = 0;

  // No localStorage persistence - always starts fresh as masked

  const togglePresentationMode = () => {
    setIsPresentationMode(prev => !prev);
    // Reset cache when toggling
    setTenantMaskCache({});
  };

  /**
   * SIMPLE PARTIAL NUMBER MASKING
   * Instead of hiding numbers, show approximate values that can't be reverse-engineered
   * Examples:
   *   156,432 → ~156K
   *   1,234,567 → ~1.2M  
   *   45 → ~50
   *   8 → <10
   */
  const maskNumber = (value) => {
    if (!isPresentationMode) {
      return typeof value === 'number' ? value.toLocaleString() : value;
    }
    
    if (typeof value !== 'number' || isNaN(value)) return '—';
    
    // Round to obscure exact values
    if (value >= 1000000) {
      return `~${(Math.round(value / 100000) / 10).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `~${Math.round(value / 1000)}K`;
    }
    if (value >= 100) {
      return `~${Math.round(value / 10) * 10}`;
    }
    if (value >= 10) {
      return `~${Math.round(value / 5) * 5}`;
    }
    return value < 5 ? '<5' : '~10';
  };

  /**
   * Mask dollar amounts - show approximate range
   * $156,432 → ~$156K
   */
  const maskAmount = (value) => {
    if (!isPresentationMode) {
      return typeof value === 'number' ? `$${value.toLocaleString()}` : value;
    }
    
    if (typeof value !== 'number' || isNaN(value)) return '$—';
    
    if (value >= 1000000) {
      return `~$${(Math.round(value / 100000) / 10).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `~$${Math.round(value / 1000)}K`;
    }
    return `~$${Math.round(value / 10) * 10}`;
  };

  /**
   * Mask percentage - round to nearest 5%
   */
  const maskPercent = (value) => {
    if (!isPresentationMode) {
      return `${value}%`;
    }
    return `~${Math.round(value / 5) * 5}%`;
  };

  /**
   * Mask a tenant/client name
   * Returns consistent "Client A", "Client B", etc. for the same tenant
   */
  const maskTenant = (tenantName, tenantId = null) => {
    if (!isPresentationMode) return tenantName;
    if (!tenantName) return 'Client';
    
    const key = tenantId || tenantName;
    
    if (tenantMaskCache[key]) {
      return tenantMaskCache[key];
    }
    
    const index = Object.keys(tenantMaskCache).length;
    const letter = ALPHABET[index % 26];
    const suffix = index >= 26 ? Math.floor(index / 26) : '';
    const masked = `Client ${letter}${suffix}`;
    
    setTenantMaskCache(prev => ({ ...prev, [key]: masked }));
    return masked;
  };

  /**
   * Mask an email address
   * "jason@company.com" → "jas***@***.com"
   */
  const maskEmail = (email) => {
    if (!isPresentationMode) return email;
    if (!email || typeof email !== 'string') return '***@***.***';
    
    const [local, domain] = email.split('@');
    if (!domain) return '***@***.***';
    
    const tld = domain.split('.').pop() || 'com';
    const maskedLocal = local.slice(0, 3) + '***';
    return `${maskedLocal}@***.${tld}`;
  };

  /**
   * Mask a person's name
   * "John Smith" → "Contact 1" or "J*** S***"
   */
  const maskName = (name, index = null) => {
    if (!isPresentationMode) return name;
    if (!name) return 'Contact';
    
    if (index !== null) {
      return `Contact ${index + 1}`;
    }
    
    // Mask each word
    const words = name.split(' ');
    return words.map(word => word.charAt(0) + '***').join(' ');
  };

  /**
   * Mask a phone number
   * "555-123-4567" → "***-***-****"
   */
  const maskPhone = (phone) => {
    if (!isPresentationMode) return phone;
    return '***-***-****';
  };

  /**
   * Mask source/origin data (forms, campaigns, traffic sources)
   * Used to hide specific source identifiers while showing category
   * "Contact Us Form - Homepage" → "Form ***"
   * "google / cpc" → "Source ***"
   */
  const maskSource = (source, sourceType = 'generic') => {
    if (!isPresentationMode) return source;
    if (!source || typeof source !== 'string') return '***';
    
    // Map common source types to generic labels
    const typeLabels = {
      form: 'Form',
      campaign: 'Campaign',
      utm: 'UTM Source',
      traffic: 'Traffic Source',
      generic: 'Source'
    };
    
    const label = typeLabels[sourceType] || typeLabels.generic;
    return `${label} ***`;
  };

  /**
   * Mask any sensitive value
   * Use this for generic masking
   */
  const maskValue = (value, type = 'generic') => {
    if (!isPresentationMode) return value;
    
    switch (type) {
      case 'email':
        return maskEmail(value);
      case 'name':
        return maskName(value);
      case 'amount':
        return maskAmount(value);
      case 'phone':
        return maskPhone(value);
      case 'tenant':
        return maskTenant(value);
      case 'source':
        return maskSource(value);
      case 'form':
        return maskSource(value, 'form');
      case 'campaign':
        return maskSource(value, 'campaign');
      default:
        // Generic masking - show first 3 chars
        if (typeof value === 'string' && value.length > 3) {
          return value.slice(0, 3) + '***';
        }
        return '***';
    }
  };

  /**
   * Get a safe display value - returns original or masked based on mode
   */
  const safe = (value, type = 'generic') => {
    return maskValue(value, type);
  };

  const value = {
    isPresentationMode,
    togglePresentationMode,
    setIsPresentationMode,
    // PII masking
    maskTenant,
    maskEmail,
    maskName,
    maskPhone,
    maskValue,
    maskSource,
    safe,
    // Number masking (partial - shows approximate values)
    maskNumber,
    maskAmount,
    maskPercent,
  };

  return (
    <PresentationModeContext.Provider value={value}>
      {children}
    </PresentationModeContext.Provider>
  );
};

export const usePresentationMode = () => {
  const context = useContext(PresentationModeContext);
  if (!context) {
    throw new Error('usePresentationMode must be used within a PresentationModeProvider');
  }
  return context;
};

export default PresentationModeContext;
